/**
 * Reads the three sources behind /looker coverage (spec §9.3) for one workspace
 * and a bounded period, on the browser client (the caller's RLS), and builds
 * the facts. THROWS on any failed read: a partial read would show as an
 * "all clear" that nobody measured.
 *
 * @module services/looker/lookerCoverageLoader
 */
import { getSupabaseClient } from '@/config';
import { readCampaignSummary } from '@/services/mediaIntelligenceShared';
import { readAllRows } from '@/services/supabasePaging';
import { coverageOf, type LookerCoverage, type McovRow, type MetricCoverageRow } from './lookerCoverage';
import type { AwarenessMetric } from '@/services/targets/resultMetric';

type Period = { start: string; end: string };

const messageOf = (error: unknown): string =>
  (error as { message?: string } | null)?.message ?? String(error);

/** Every platform-day in the period. (platform, date) is the RPC's GROUP BY key, so the order is unique. */
const readDays = async (workspaceId: string, period: Period): Promise<McovRow[]> => {
  const { rows, error } = await readAllRows<McovRow>(
    (from, to) =>
      getSupabaseClient()
        .rpc('get_mmm_coverage', { p_workspace_ids: [workspaceId] })
        .gte('date', period.start)
        .lte('date', period.end)
        .order('platform', { ascending: true })
        .order('date', { ascending: true })
        .range(from, to),
    'loadCoverage days',
  );
  if (error) throw new Error(`coverage: day read failed: ${messageOf(error)}`);
  return rows;
};

/**
 * Non-NULL counts per (platform, objective) — migration 115. One call: the
 * result is at most platforms × objectives rows, far under the 1000-row cap.
 */
const readMetricCoverage = async (workspaceId: string, period: Period): Promise<MetricCoverageRow[]> => {
  const { data, error } = await getSupabaseClient()
    .rpc('get_metric_coverage', { p_workspace_id: workspaceId, p_start: period.start, p_end: period.end })
    .order('platform', { ascending: true })
    .order('objective', { ascending: true });
  if (error) throw new Error(`coverage: metric coverage read failed: ${messageOf(error)}`);
  return (data ?? []) as MetricCoverageRow[];
};

/** `awareness`: DEC3's Awareness Result metric in force (payload.resultMapping); null = unread. Required: never a guessed default. */
export const loadCoverage = async (workspaceId: string, period: Period, awareness: AwarenessMetric | null): Promise<LookerCoverage> => {
  const summary = await readCampaignSummary({ workspaceIds: [workspaceId] }, period.start, period.end, 'loadCoverage');
  if (summary.error) throw new Error(`coverage: campaign summary read failed: ${messageOf(summary.error)}`);
  const [metrics, days] = await Promise.all([readMetricCoverage(workspaceId, period), readDays(workspaceId, period)]);
  return coverageOf(summary.rows, metrics, days, period, awareness);
};
